import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import { Provider } from 'react-redux';
import { store } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { useSessionValidator } from '@/hooks/useSessionValidator';
import { setupCrossTabSync } from '@/store/persistMiddleware';
import { lightTheme, darkTheme } from '@/styles/theme';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { PrivacyProvider } from '@/contexts/PrivacyContext';
import { AppLayout } from '@/components/layout';
import '@/styles/global.css';

// Eager — needed for first paint: the unauth landing and the post-login
// default route. Everything else is code-split below so the initial bundle
// stays small (login/dashboard only).
import { LoginPage } from '@/pages/Login/LoginPage';
import { DashboardPage } from '@/pages/Dashboard/DashboardPage';
// Demo entry is eager (not code-split) so the marketing deep-link enters demo
// with no chunk-load flash. The fixtures themselves are imported by the entry
// component, so they still only load when /demo is actually visited.
import { DemoEntry } from '@/demo/DemoEntry';
import { enterDemoMode } from '@/store/authSlice';
import { seedDemoStore, hasDemoSession } from '@/demo/seedDemoStore';
import { isShareRoutePath, SHARE_ROUTE_PATH } from '@/lib/sharedProposal';

// Lazy route chunks — fetched on demand, kept out of the initial bundle.
// Imports from each barrel dedupe to one shared chunk per section.
const OrganizationsPage = lazy(() =>
  import('@/pages/Organizations').then((m) => ({ default: m.OrganizationsPage }))
);
const MembersPage = lazy(() =>
  import('@/pages/Organizations').then((m) => ({ default: m.MembersPage }))
);
const InvitationsPage = lazy(() =>
  import('@/pages/Organizations').then((m) => ({ default: m.InvitationsPage }))
);
const SettingsPage = lazy(() =>
  import('@/pages/Organizations').then((m) => ({ default: m.SettingsPage }))
);
const OrgPoliciesPage = lazy(() =>
  import('@/pages/Organizations').then((m) => ({ default: m.OrgPoliciesPage }))
);
const ApprovalGroupsPage = lazy(() =>
  import('@/pages/Organizations').then((m) => ({ default: m.ApprovalGroupsPage }))
);
const PolicyChangesPage = lazy(() =>
  import('@/pages/Organizations').then((m) => ({ default: m.PolicyChangesPage }))
);
const IntegrationsPage = lazy(() =>
  import('@/pages/Integrations').then((m) => ({ default: m.IntegrationsPage }))
);
const NotificationsPage = lazy(() =>
  import('@/pages/Invitations').then((m) => ({ default: m.NotificationsPage }))
);
const ProfilePage = lazy(() => import('@/pages/Profile').then((m) => ({ default: m.ProfilePage })));
const ActivityOverviewPage = lazy(() =>
  import('@/pages/Activity').then((m) => ({ default: m.ActivityOverviewPage }))
);
const AuditLogPage = lazy(() =>
  import('@/pages/Activity').then((m) => ({ default: m.AuditLogPage }))
);
const VaultAuditLogPage = lazy(() =>
  import('@/pages/Activity').then((m) => ({ default: m.VaultAuditLogPage }))
);
const CriticalActionsPage = lazy(() =>
  import('@/pages/Activity').then((m) => ({ default: m.CriticalActionsPage }))
);
const LoginActivityPage = lazy(() =>
  import('@/pages/Activity').then((m) => ({ default: m.LoginActivityPage }))
);
const VaultsPage = lazy(() => import('@/pages/Vaults').then((m) => ({ default: m.VaultsPage })));
const CreateVaultPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.CreateVaultPage }))
);
const VaultLayout = lazy(() => import('@/pages/Vaults').then((m) => ({ default: m.VaultLayout })));
const VaultDetailPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultDetailPage }))
);
const VaultTransactionsPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultTransactionsPage }))
);
const VaultProposalsPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultProposalsPage }))
);
const NewProposalPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.NewProposalPage }))
);
const ProposalDetailPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.ProposalDetailPage }))
);
const VaultSignatureRequestsPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultSignatureRequestsPage }))
);
const SignatureRequestDetailPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.SignatureRequestDetailPage }))
);
const VaultAddressesPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultAddressesPage }))
);
const AddressDetailPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.AddressDetailPage }))
);
const VaultMembersPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultMembersPage }))
);
const VaultSettingsPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultSettingsPage }))
);
const VaultPoliciesPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultPoliciesPage }))
);
const VaultSetupPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultSetupPage }))
);
const SigningRequestsPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.SigningRequestsPage }))
);
const RecurringPaymentsPage = lazy(() =>
  import('@/pages/RecurringPayments').then((m) => ({ default: m.RecurringPaymentsPage }))
);
const RecurringPaymentFormPage = lazy(() =>
  import('@/pages/RecurringPayments').then((m) => ({ default: m.RecurringPaymentFormPage }))
);
const RecurringPaymentDetailPage = lazy(() =>
  import('@/pages/RecurringPayments').then((m) => ({ default: m.RecurringPaymentDetailPage }))
);
const TransfersActivityPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.TransfersActivityPage }))
);
const PortfolioOverviewPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.PortfolioOverviewPage }))
);
const VaultActivityPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultActivityPage }))
);
const VaultContactsPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultContactsPage }))
);
const VaultTokensPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultTokensPage }))
);
const VaultFluxNodesPage = lazy(() =>
  import('@/pages/Vaults').then((m) => ({ default: m.VaultFluxNodesPage }))
);
const ContactsPage = lazy(() =>
  import('@/pages/Contacts').then((m) => ({ default: m.ContactsPage }))
);
const SpamThreatsPage = lazy(() =>
  import('@/pages/SpamThreats/SpamThreatsPage').then((m) => ({ default: m.SpamThreatsPage }))
);
const BillingPage = lazy(() =>
  import('@/pages/Billing/BillingPage').then((m) => ({ default: m.BillingPage }))
);
const InvoiceDetailPage = lazy(() =>
  import('@/pages/Billing/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage }))
);
const PricingPage = lazy(() =>
  import('@/pages/Billing/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const TermsPage = lazy(() => import('@/pages/Legal').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('@/pages/Legal').then((m) => ({ default: m.PrivacyPage })));
const AnalyticsPage = lazy(() =>
  import('@/pages/Analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage }))
);
// Public, read-only payment status for a payee (share link). No session.
const SharedProposalPage = lazy(() =>
  import('@/pages/Share').then((m) => ({ default: m.SharedProposalPage }))
);

function PageFallback() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Spin size="large" />
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/demo" element={<DemoEntry />} />
        <Route path="/terms-of-service" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />
        <Route path={SHARE_ROUTE_PATH} element={<SharedProposalPage />} />
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/invitations" element={<InvitationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/org-policies" element={<OrgPoliciesPage />} />
          <Route path="/approval-groups" element={<ApprovalGroupsPage />} />
          <Route path="/policy-changes" element={<PolicyChangesPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/spam-threats" element={<SpamThreatsPage />} />
          <Route path="/portfolio" element={<PortfolioOverviewPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/vaults" element={<VaultsPage />} />
          <Route path="/vaults/create" element={<CreateVaultPage />} />
          <Route path="/vaults/:vaultId" element={<VaultLayout />}>
            <Route index element={<VaultDetailPage />} />
            <Route path="transactions" element={<VaultTransactionsPage />} />
            <Route path="proposals" element={<VaultProposalsPage />} />
            <Route path="proposals/new" element={<NewProposalPage />} />
            <Route path="proposals/:proposalId" element={<ProposalDetailPage />} />
            <Route path="signature-requests" element={<VaultSignatureRequestsPage />} />
            <Route path="signature-requests/:requestId" element={<SignatureRequestDetailPage />} />
            <Route path="addresses" element={<VaultAddressesPage />} />
            <Route path="addresses/:addressIndex" element={<AddressDetailPage />} />
            <Route path="members" element={<VaultMembersPage />} />
            <Route path="contacts" element={<VaultContactsPage />} />
            <Route path="activity" element={<VaultActivityPage />} />
            <Route path="tokens" element={<VaultTokensPage />} />
            <Route path="nodes" element={<VaultFluxNodesPage />} />
            <Route path="settings" element={<VaultSettingsPage />} />
            <Route path="policies" element={<VaultPoliciesPage />} />
          </Route>
          <Route path="/vault-setup" element={<VaultSetupPage />} />
          <Route path="/signing-requests" element={<SigningRequestsPage />} />
          <Route path="/transfers" element={<TransfersActivityPage />} />
          <Route path="/recurring-payments" element={<RecurringPaymentsPage />} />
          <Route path="/recurring-payments/new" element={<RecurringPaymentFormPage />} />
          <Route path="/recurring-payments/:paymentId" element={<RecurringPaymentDetailPage />} />
          <Route
            path="/recurring-payments/:paymentId/edit"
            element={<RecurringPaymentFormPage />}
          />
          <Route path="/activity" element={<ActivityOverviewPage />} />
          <Route path="/activity/audit-log" element={<AuditLogPage />} />
          <Route path="/activity/vault-activity" element={<VaultAuditLogPage />} />
          <Route path="/activity/critical-actions" element={<CriticalActionsPage />} />
          <Route path="/activity/login-activity" element={<LoginActivityPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/invoices/:invoiceId" element={<InvoiceDetailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
        </Route>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

// Re-enter the demo on a mid-demo refresh (§10.2): a tab-scoped sessionStorage
// flag survives reload while Redux does not. We rehydrate synchronously at
// module/render time (before the first route resolves) so /dashboard renders
// instead of bouncing to /login. Real auth (a persisted session) always wins —
// we never re-enter demo over a logged-in tab.
function maybeRehydrateDemo() {
  const state = store.getState();
  if (state.auth.isDemo) return;
  if (state.auth.isAuthenticated) return;
  if (!hasDemoSession()) return;
  store.dispatch(enterDemoMode());
  seedDemoStore(store.dispatch);
}

// The public share page must work logged-out and never call authenticated
// endpoints, so it skips the stored-session check. It links out only in new
// tabs, so this load never navigates into the app without a full reload.
const IS_PUBLIC_SHARE_LOAD = isShareRoutePath(window.location.pathname);

function ThemedApp() {
  const isDark = useAppSelector((state) => state.theme.isDark);
  const { isValidating } = useSessionValidator({ enabled: !IS_PUBLIC_SHARE_LOAD });

  // Sync data-theme attribute with theme state for CSS variables
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Cross-tab localStorage sync
  useEffect(() => {
    return setupCrossTabSync(store.dispatch, () => store.getState());
  }, []);

  // Show loading spinner while validating session on app load
  if (isValidating) {
    return (
      <ConfigProvider theme={isDark ? darkTheme : lightTheme} wave={{ disabled: true }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Spin size="large" />
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={isDark ? darkTheme : lightTheme} wave={{ disabled: true }}>
      <AntApp>
        <PrivacyProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </PrivacyProvider>
      </AntApp>
    </ConfigProvider>
  );
}

// Rehydrate demo BEFORE the first render so the initial route resolves with the
// correct auth/demo state (no flash bounce to /login on a mid-demo refresh).
maybeRehydrateDemo();

export function App() {
  return (
    <Provider store={store}>
      <ErrorBoundary>
        <ThemedApp />
      </ErrorBoundary>
    </Provider>
  );
}

export default App;
